import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaPaperPlane,
  FaSpinner,
  FaSave,
  FaCheckCircle,
  FaExclamationTriangle,
  FaVolumeUp,
  FaClock,
  FaArrowRight,
  FaArrowLeft,
  FaGraduationCap,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";
import ExamTimer from "../../exam/ExamTimer";
import TeilDisplay from "../../exam/TeilDisplay";
import ExamSubmitConfirmation from "../../exam/ExamSubmitConfirmation";
import HorenAudioPlayer from "./HorenAudioPlayer";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ===== TYPE DEFINITIONS =====

type QuestionType =
  | "MULTIPLE_CHOICE"
  | "MULTIPLE_CHOICE_4"
  | "TRUE_FALSE"
  | "TEXT_INPUT"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "AUDIO_MULTIPLE_CHOICE"
  | "AUDIO_TRUE_FALSE"
  | "MATCHING"
  | "DRAG_DROP"
  | "FILL_BLANKS"
  | "SENTENCE_ORDER";

type ExamPart = {
  id: number;
  examSetId: number;
  module: string;
  partNumber: number;
  displayOrder: number;
  instructions: string;
  partTitle?: string;
  contextText?: string;
  contextImageUrl?: string;
  contextImageDownloadUrl?: string;
  contextDocumentUrl?: string;
  contextDocumentDownloadUrl?: string;
  contextAudioUrl?: string;
  contextAudioDownloadUrl?: string;
  recommendedTimeMinutes?: number;
  totalPoints: number;
  questionCount: number;
  displayName: string;
  shortDisplayName: string;
  hasContextText: boolean;
  hasContextImage: boolean;
  hasContextDocument: boolean;
  hasContextAudio: boolean;
  hasSharedContext: boolean;
};

type Question = {
  id: number;
  examSetId: number;
  examPartId: number;
  module: string;
  partNumber: number;
  questionNumber: number;
  questionType: QuestionType;
  questionText: string;
  contextText?: string;
  audioUrl?: string;
  audioDownloadUrl?: string;
  imageUrl?: string;
  imageDownloadUrl?: string;
  effectiveContextText?: string;
  effectiveAudioUrl?: string;
  effectiveAudioDownloadUrl?: string;
  options: string[] | string;
  points: number;
  usesSharedContext: boolean;
  hasQuestionAudio: boolean;
  hasQuestionImage: boolean;
  displayLabel: string;
  fullDisplayLabel: string;
  isAutoGradable: boolean;
};

type Answer = {
  questionId: number;
  answer: string;
  timeSpentSeconds?: number;
};

// ✅ Backend uses HOREN (without umlaut)
type ExamModule = "LESEN" | "HOREN" | "SCHREIBEN";

const StudentTakeExam: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [examDetails, setExamDetails] = useState<any>(null);
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [examName, setExamName] = useState<string>("");
  const [parts, setParts] = useState<ExamPart[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Map<number, string>>(new Map());
  const [currentModule, setCurrentModule] = useState<ExamModule>("LESEN");
  const [availableModules] = useState<ExamModule[]>([
    "LESEN",
    "HOREN", // ✅ Backend value
    "SCHREIBEN",
  ]);
  const [mustEndByTimestamp, setMustEndByTimestamp] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loadingModule, setLoadingModule] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [error, setError] = useState("");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [visitedModules, setVisitedModules] = useState<Set<ExamModule>>(
    new Set(["LESEN"] as ExamModule[])
  );
  const [horenAudioUrl, setHorenAudioUrl] = useState<string | null>(null);

  const initializingRef = React.useRef(false);

  // ✅ Helper function to display module names with umlaut
  const getModuleDisplayName = (module: ExamModule): string => {
    const displayNames: Record<ExamModule, string> = {
      LESEN: "LESEN",
      HOREN: "HÖREN", // ✅ Display with umlaut
      SCHREIBEN: "SCHREIBEN",
    };
    return displayNames[module];
  };

  // ✅ NEW: Get previous module
  const getPreviousModule = (): ExamModule | null => {
    const currentIndex = availableModules.indexOf(currentModule);
    if (currentIndex > 0) {
      return availableModules[currentIndex - 1];
    }
    return null;
  };

  // ✅ NEW: Get next module
  const getNextModule = (): ExamModule | null => {
    const currentIndex = availableModules.indexOf(currentModule);
    if (currentIndex < availableModules.length - 1) {
      return availableModules[currentIndex + 1];
    }
    return null;
  };

  useEffect(() => {
    if (!initializingRef.current) {
      initializingRef.current = true;
      initializeExam();
    }
  }, [assignmentId]);

  const initializeExam = async () => {
    setLoading(true);
    setError("");

    try {
      const detailsRes = await fetch(
        `${API_BASE_URL}/api/student/exams/${assignmentId}`,
        { credentials: "include" }
      );

      if (!detailsRes.ok) {
        throw new Error("Failed to load exam details");
      }

      const detailsData = await detailsRes.json();
      const details = detailsData.data || detailsData;
      setExamDetails(details);

      try {
        const audioRes = await fetch(
          `${API_BASE_URL}/api/student/exams/${assignmentId}/horen-audio`,
          { credentials: "include" }
        );

        if (audioRes.ok) {
          const audioData = await audioRes.json();
          const audio = audioData.data || audioData;

          if (audio.horenAudioStreamUrl) {
            setHorenAudioUrl(audio.horenAudioStreamUrl);
          }
        }
      } catch (err) {
        console.warn("Failed to load Hören audio:", err);
      }

      if (details.hasAttempt && details.attemptId) {
        await resumeExistingAttempt(details.attemptId, details);
      } else {
        await startNewAttempt(details);
      }
    } catch (err: any) {
      console.error("Failed to initialize exam:", err);
      setError(err.message || "Failed to load exam");
      initializingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const startNewAttempt = async (details: any) => {
    try {
      const startRes = await fetch(
        `${API_BASE_URL}/api/student/exams/${assignmentId}/start`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (!startRes.ok) {
        const errorData = await startRes.json();
        throw new Error(errorData.message || "Failed to start exam");
      }

      const startData = await startRes.json();
      const startResponse = startData.data || startData;

      setAttemptId(startResponse.attemptId);
      setExamName(startResponse.examName);
      setMustEndByTimestamp(startResponse.mustEndByTimestamp);

      const remaining = Math.max(
        0,
        Math.floor((startResponse.mustEndByTimestamp - Date.now()) / 1000)
      );
      setTimeRemaining(remaining);

      await loadPartsAndQuestionsForModule("LESEN", startResponse.attemptId);
    } catch (err: any) {
      throw new Error(err.message || "Failed to start exam");
    }
  };

  const resumeExistingAttempt = async (
    existingAttemptId: number,
    details: any
  ) => {
    try {
      setAttemptId(existingAttemptId);
      setExamName(details.examSetName);

      const progressRes = await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${existingAttemptId}/progress`,
        { credentials: "include" }
      );

      if (progressRes.ok) {
        const progressData = await progressRes.json();
        const progress = progressData.data || progressData;

        const currentMod = (progress.currentModule || "LESEN") as ExamModule;
        setCurrentModule(currentMod);
        setTimeRemaining(progress.timeRemainingSeconds || 0);

        const mustEndBy = Date.now() + progress.timeRemainingSeconds * 1000;
        setMustEndByTimestamp(mustEndBy);
      }

      await loadPartsAndQuestionsForModule(currentModule, existingAttemptId);
    } catch (err: any) {
      console.error("Failed to resume attempt:", err);
      throw new Error("Failed to resume exam");
    }
  };

  const loadPartsAndQuestionsForModule = async (
    module: ExamModule,
    attemptIdToUse?: number
  ) => {
    const targetAttemptId = attemptIdToUse || attemptId;

    if (!targetAttemptId) {
      console.error("No attempt ID available");
      return;
    }

    setLoadingModule(true);
    setError("");

    try {
      // ✅ FIXED: Use parts endpoint instead of questions endpoint
      const partsRes = await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${targetAttemptId}/parts?module=${module}`,
        { credentials: "include" }
      );

      if (!partsRes.ok) {
        throw new Error(`Failed to load questions for ${module}`);
      }

      const partsData = await partsRes.json();
      const loadedParts: ExamPart[] = partsData.data || partsData;

      // ✅ Extract all questions from parts
      const allQuestions: Question[] = [];
      loadedParts.forEach((part: any) => {
        if (part.questions && Array.isArray(part.questions)) {
          allQuestions.push(...part.questions);
        }
      });

      console.log(
        `✅ Loaded ${loadedParts.length} parts with ${allQuestions.length} questions for ${module}`
      );
      console.log("Parts data:", loadedParts); // Debug log to see context

      setQuestions(allQuestions);
      setParts(loadedParts); // ✅ Parts now have FULL context data!
    } catch (err: any) {
      console.error(`Failed to load questions for ${module}:`, err);
      setError(err.message || `Failed to load questions for ${module}`);
    } finally {
      setLoadingModule(false);
    }
  };

  const handleModuleChange = async (newModule: ExamModule) => {
    if (newModule === currentModule) return;

    // Save current answers before switching
    await saveAllAnswers(true);

    setCurrentModule(newModule);
    // ✅ REMOVED: setAnswers(new Map()); - Keep all answers in memory!
    setVisitedModules((prev) => new Set(prev).add(newModule));

    // Load questions for new module (but keep existing answers)
    await loadPartsAndQuestionsForModule(newModule);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (timeRemaining <= 0 || isSubmitted) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isSubmitted]);

  useEffect(() => {
    if (!attemptId || answers.size === 0 || isSubmitted) return;

    const autoSaveTimer = setInterval(() => {
      saveAllAnswers(true);
    }, 30000);

    return () => clearInterval(autoSaveTimer);
  }, [attemptId, answers, isSubmitted]);

  const saveAllAnswers = async (isAutoSave = false) => {
    if (!attemptId || answers.size === 0) return;

    if (isAutoSave) setAutoSaving(true);

    try {
      const answerList: Answer[] = Array.from(answers.entries()).map(
        ([questionId, studentAnswer]) => ({
          questionId,
          answer: studentAnswer,
        })
      );

      await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${attemptId}/answers/batch`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers: answerList }),
        }
      );
    } catch (err) {
      console.error("Auto-save failed:", err);
      if (!isAutoSave) {
        throw err;
      }
    } finally {
      if (isAutoSave) setAutoSaving(false);
    }
  };

  const handleAnswerChange = (questionId: number, answer: string) => {
    setAnswers(prev => new Map(prev).set(questionId, answer));
  };

  const handleAutoSubmit = async () => {
    if (submitting || isSubmitted) return;

    setSubmitting(true);
    setIsSubmitted(true);

    try {
      await saveAllAnswers();

      const response = await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${attemptId}/submit`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert(
          "⏰ Zeit abgelaufen! Ihre Prüfung wurde automatisch eingereicht."
        );
        navigate(`/student/my-exams`);
      } else {
        throw new Error("Failed to auto-submit exam");
      }
    } catch (err) {
      console.error("Auto-submit failed:", err);
      setError("Failed to submit exam. Please try again.");
      setIsSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClick = () => {
    setShowSubmitConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setIsSubmitted(true);

    try {
      await saveAllAnswers();

      const response = await fetch(
        `${API_BASE_URL}/api/student/exams/attempts/${attemptId}/submit`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        navigate(`/student/my-exams`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit exam");
      }
    } catch (err: any) {
      console.error("Submit failed:", err);
      setError(err.message || "Failed to submit exam. Please try again.");
      setIsSubmitted(false);
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  const moduleQuestions = questions.filter((q) => q.module === currentModule);
  const moduleParts = parts.filter((p) => p.module === currentModule);
  const answeredCount = moduleQuestions.filter((q) => answers.has(q.id)).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
          <p className="text-gray-600 text-base sm:text-lg">
            Prüfung wird geladen...
          </p>
        </div>
      </div>
    );
  }

  if (error && !attemptId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-4xl sm:text-5xl mb-4">⚠️</div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Prüfung kann nicht geladen werden
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate("/student/my-exams")}
              className="w-full sm:w-auto px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-all"
            >
              Zurück zu Meine Prüfungen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const previousModule = getPreviousModule();
  const nextModule = getNextModule();

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaGraduationCap />}
        title={examName || "Exam"}
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "My Exams", to: "/student/my-exams" },
          { label: getModuleDisplayName(currentModule) },
        ]}
      />

      {/* Exam progress & timer */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-gray-600 font-medium">
            {getModuleDisplayName(currentModule)} • {answeredCount}/
            {moduleQuestions.length} beantwortet
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {autoSaving && (
              <div className="hidden sm:flex items-center gap-2 text-gray-500 text-sm">
                <FaSave className="animate-pulse" />
                <span>Speichert...</span>
              </div>
            )}
            <div className="bg-gray-100 rounded-lg px-3 py-2 border border-gray-200">
              <div className="flex items-center gap-2 text-gray-800">
                <FaClock className="text-sm sm:text-base" />
                <ExamTimer
                  endTime={new Date(mustEndByTimestamp).toISOString()}
                  onTimeUp={handleAutoSubmit}
                  isSubmitted={isSubmitted}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Module Tabs at Top */}
        <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-1 px-1">
          {availableModules.map((module) => {
            const moduleQuestionCount = questions.filter(
              (q) => q.module === module
            ).length;
            const moduleAnsweredCount = questions.filter(
              (q) => q.module === module && answers.has(q.id)
            ).length;

            return (
              <button
                key={module}
                onClick={() => handleModuleChange(module)}
                disabled={loadingModule}
                className={`flex-shrink-0 min-w-[90px] sm:min-w-[120px] px-4 sm:px-6 py-3 rounded-lg font-semibold text-sm sm:text-base transition-all relative ${
                  currentModule === module
                    ? "bg-[#009fe3] text-white shadow-lg scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md"
                } ${loadingModule ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <div className="font-bold">{getModuleDisplayName(module)}</div>
                {moduleQuestionCount > 0 && (
                  <div className="text-xs mt-1 opacity-90">
                    {moduleAnsweredCount}/{moduleQuestionCount}
                  </div>
                )}
                {currentModule !== module &&
                  moduleAnsweredCount === moduleQuestionCount &&
                  moduleQuestionCount > 0 && (
                    <FaCheckCircle className="absolute top-2 right-2 text-green-500 text-sm" />
                  )}
              </button>
            );
          })}
        </div>

        {/* Hören Audio Player */}
        {currentModule === "HOREN" && horenAudioUrl && (
          <div className="mb-4 sm:mb-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-2 border-indigo-300 rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-3 sm:py-4">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white flex items-center gap-2 sm:gap-3">
                <FaVolumeUp className="text-lg sm:text-2xl flex-shrink-0" />
                <span className="truncate">Hören Module Audio</span>
              </h3>
              <p className="text-indigo-100 text-xs sm:text-sm mt-1">
                ⚠️ Dieses Audio kann nur EINMAL abgespielt werden
              </p>
            </div>

            <div className="p-4 sm:p-6">
              <HorenAudioPlayer audioUrl={horenAudioUrl} />
            </div>
          </div>
        )}

        {loadingModule && (
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center">
            <FaSpinner className="animate-spin text-[#009fe3] text-2xl sm:text-3xl mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-600">
              Fragen für {getModuleDisplayName(currentModule)} werden geladen...
            </p>
          </div>
        )}

        {!loadingModule && (
          <div className="space-y-6 sm:space-y-8">
            {moduleParts.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-500">
                  Keine Fragen für dieses Modul verfügbar.
                </p>
              </div>
            ) : (
              moduleParts.map((part) => {
                const partQuestions = questions.filter(
                  (q) => q.examPartId === part.id
                );

                return (
                  <TeilDisplay
                    key={part.id}
                    part={part}
                    questions={partQuestions}
                    answers={answers}
                    onAnswerChange={handleAnswerChange}
                    disabled={isSubmitted}
                  />
                );
              })
            )}
          </div>
        )}

        {/* ✅ NEW: Simple Back/Forward Navigation at Bottom */}
        {!loadingModule && moduleParts.length > 0 && (
          <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Navigation</h3>
            <div className="flex gap-3">
              {/* Previous Button */}
              {previousModule ? (
                <button
                  onClick={() => handleModuleChange(previousModule)}
                  disabled={loadingModule}
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg font-bold text-sm sm:text-base hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <FaArrowLeft />
                  <span className="hidden sm:inline">
                    Zurück zu {getModuleDisplayName(previousModule)}
                  </span>
                  <span className="sm:hidden">
                    {getModuleDisplayName(previousModule)}
                  </span>
                </button>
              ) : (
                <div className="flex-1"></div>
              )}

              {/* Next Button */}
              {nextModule && (
                <button
                  onClick={() => handleModuleChange(nextModule)}
                  disabled={loadingModule}
                  className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white rounded-lg font-bold text-sm sm:text-base hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span className="hidden sm:inline">
                    Weiter zu {getModuleDisplayName(nextModule)}
                  </span>
                  <span className="sm:hidden">
                    {getModuleDisplayName(nextModule)}
                  </span>
                  <FaArrowRight />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-lg p-4 sm:p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-4 text-sm sm:text-base">
              {error}
            </div>
          )}

          {visitedModules.size < 3 && (
            <div className="bg-orange-50 border-l-4 border-orange-500 p-3 sm:p-4 mb-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <FaExclamationTriangle className="text-orange-600 text-base sm:text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-orange-900 mb-1 text-sm sm:text-base">
                    ⚠️ Warnung: Nicht alle Module besucht
                  </h4>
                  <p className="text-xs sm:text-sm text-orange-800">
                    Sie haben nur <strong>{visitedModules.size} von 3</strong>{" "}
                    Modulen besucht:
                  </p>
                  <ul className="mt-2 space-y-1 text-xs sm:text-sm">
                    <li
                      className={
                        visitedModules.has("LESEN")
                          ? "text-green-700 font-semibold"
                          : "text-red-700 font-semibold"
                      }
                    >
                      {visitedModules.has("LESEN") ? "✓" : "✗"} LESEN
                    </li>
                    <li
                      className={
                        visitedModules.has("HOREN")
                          ? "text-green-700 font-semibold"
                          : "text-red-700 font-semibold"
                      }
                    >
                      {visitedModules.has("HOREN") ? "✓" : "✗"} HÖREN
                    </li>
                    <li
                      className={
                        visitedModules.has("SCHREIBEN")
                          ? "text-green-700 font-semibold"
                          : "text-red-700 font-semibold"
                      }
                    >
                      {visitedModules.has("SCHREIBEN") ? "✓" : "✗"} SCHREIBEN
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4">
            <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">
              Vor der Abgabe:
            </h4>
            <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
              <li
                className={
                  visitedModules.size === 3
                    ? "text-green-700 font-semibold"
                    : ""
                }
              >
                {visitedModules.size === 3 ? "✓" : "○"} Alle drei Module prüfen
              </li>
              <li>✓ Alle Fragen beantwortet</li>
              <li>✓ Antworten sorgfältig überprüfen</li>
              <li>⚠️ Nach der Abgabe keine Änderungen möglich</li>
            </ul>
          </div>

          <button
            onClick={handleSubmitClick}
            disabled={submitting || isSubmitted || visitedModules.size < 3}
            className="w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold text-base sm:text-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? (
              <>
                <FaSpinner className="animate-spin" />
                <span className="hidden sm:inline">Wird eingereicht...</span>
                <span className="sm:hidden">Einreichen...</span>
              </>
            ) : visitedModules.size < 3 ? (
              <>
                <FaExclamationTriangle />
                <span className="hidden sm:inline">
                  Besuchen Sie zuerst alle Module
                </span>
                <span className="sm:hidden">Alle Module besuchen</span>
              </>
            ) : (
              <>
                <FaPaperPlane />
                Prüfung abgeben
              </>
            )}
          </button>
          <p className="text-center text-xs sm:text-sm text-gray-500 mt-2 sm:mt-3">
            {visitedModules.size < 3
              ? `${visitedModules.size}/3 Module besucht`
              : "Alle Module überprüfen vor der Abgabe"}
          </p>
        </div>
      </main>

      <ExamSubmitConfirmation
        isOpen={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleConfirmSubmit}
        totalQuestions={questions.length}
        answeredQuestions={answers.size}
        isSubmitting={submitting}
        timeRemaining={timeRemaining}
      />
    </div>
  );
};

export default StudentTakeExam;
